import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { EXPENSES_DIRTY_KEY, getExpensesDirtyMarker } from '../lib/expensesCache';

const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // reconciliación completa cada 6h como máximo
const DELTA_BATCH_SIZE = 250;
const CACHE_PREFIX = 'expenses:v2:';

const timestampToMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof value === 'object' && Number.isFinite(value.seconds)) {
    return Number(value.seconds) * 1000 + Math.floor(Number(value.nanoseconds || 0) / 1e6);
  }
  return 0;
};

const normalizeForCache = (expense) => {
  const clone = { ...expense };
  ['date', 'createdAt', 'updatedAt', 'deletedAt'].forEach((key) => {
    if (clone[key]) {
      const millis = timestampToMillis(clone[key]);
      if (millis) clone[key] = millis;
    }
  });
  if (clone.support?.createdAt) {
    const millis = timestampToMillis(clone.support.createdAt);
    clone.support = { ...clone.support, ...(millis ? { createdAt: millis } : {}) };
  }
  return clone;
};

const sortExpenses = (rows) => [...rows].sort((a, b) => {
  const ua = timestampToMillis(a.updatedAt) || timestampToMillis(a.createdAt);
  const ub = timestampToMillis(b.updatedAt) || timestampToMillis(b.createdAt);
  if (ua !== ub) return ub - ua;
  const ad = String(a.dateStr || '');
  const bd = String(b.dateStr || '');
  if (ad !== bd) return bd.localeCompare(ad);
  return String(b.id || '').localeCompare(String(a.id || ''));
});

export function useExpensesData({ startDate, endDate, affectsCash = null } = {}) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const metaRef = useRef({ cacheTs: 0, fullSyncAt: 0, lastUpdatedAtMs: 0 });

  const range = useMemo(() => {
    if (!startDate && !endDate) return null;
    const startYMD = startDate || endDate;
    const endYMD = endDate || startYMD;
    return { startYMD, endYMD };
  }, [startDate, endDate]);

  const cacheKey = useMemo(() => {
    const rangeKey = range ? `${range.startYMD}:${range.endYMD}` : 'all';
    const cashFlag = affectsCash === null ? 'all' : affectsCash ? 'cash' : 'nocash';
    return `${CACHE_PREFIX}${rangeKey}:${cashFlag}`;
  }, [range, affectsCash]);

  const readCache = useCallback(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed?.data)) return null;
      return {
        data: parsed.data,
        ts: Number(parsed.ts || 0),
        fullSyncAt: Number(parsed.fullSyncAt || 0),
        lastUpdatedAtMs: Number(parsed.lastUpdatedAtMs || 0),
      };
    } catch {
      return null;
    }
  }, [cacheKey]);

  const writeCache = useCallback((rows, meta = {}) => {
    if (typeof window === 'undefined') return;
    const nextMeta = {
      ...metaRef.current,
      ...meta,
      cacheTs: Date.now(),
    };
    metaRef.current = nextMeta;
    try {
      localStorage.setItem(cacheKey, JSON.stringify({
        data: rows.map(normalizeForCache),
        ts: nextMeta.cacheTs,
        fullSyncAt: nextMeta.fullSyncAt || 0,
        lastUpdatedAtMs: nextMeta.lastUpdatedAtMs || 0,
      }));
    } catch (cacheError) {
      console.warn('No se pudo persistir la caché de gastos', cacheError);
    }
  }, [cacheKey]);

  const buildConstraints = useCallback(() => {
    const constraints = [];
    if (range?.startYMD) constraints.push(where('dateStr', '>=', range.startYMD));
    if (range?.endYMD) constraints.push(where('dateStr', '<=', range.endYMD));
    if (affectsCash !== null) constraints.push(where('affectsCash', '==', affectsCash));
    return constraints;
  }, [range?.startYMD, range?.endYMD, affectsCash]);

  const fetchFull = useCallback(async () => {
    const constraints = buildConstraints();
    const snap = await getDocs(query(collection(db, 'gastos'), ...constraints));
    const rows = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
    const sorted = sortExpenses(rows);
    const lastUpdatedAtMs = sorted.reduce(
      (max, row) => Math.max(max, timestampToMillis(row.updatedAt)),
      0
    );
    const now = Date.now();
    setExpenses(sorted);
    writeCache(sorted, { fullSyncAt: now, lastUpdatedAtMs });
    return sorted;
  }, [buildConstraints, writeCache]);

  const fetchDelta = useCallback(async (baseRows, lastUpdatedAtMs) => {
    if (!lastUpdatedAtMs) return fetchFull();

    const mergeMap = new Map(baseRows.map((row) => [row.id, row]));
    let lastDoc = null;
    let newestUpdatedAtMs = lastUpdatedAtMs;

    while (true) {
      const constraints = [
        where('updatedAt', '>', Timestamp.fromMillis(lastUpdatedAtMs)),
        orderBy('updatedAt', 'asc'),
      ];
      if (lastDoc) constraints.push(startAfter(lastDoc));
      constraints.push(limit(DELTA_BATCH_SIZE));

      const snap = await getDocs(query(collection(db, 'gastos'), ...constraints));
      for (const docSnap of snap.docs) {
        const row = { id: docSnap.id, ...docSnap.data() };
        const ymd = String(row.dateStr || '');
        const inRange = (!range?.startYMD || ymd >= range.startYMD)
          && (!range?.endYMD || ymd <= range.endYMD);
        const cashMatches = affectsCash === null || !!row.affectsCash === affectsCash;
        if (inRange && cashMatches) mergeMap.set(row.id, row);
        else mergeMap.delete(row.id);
        newestUpdatedAtMs = Math.max(newestUpdatedAtMs, timestampToMillis(row.updatedAt));
      }

      if (snap.empty || snap.size < DELTA_BATCH_SIZE) break;
      lastDoc = snap.docs[snap.docs.length - 1];
    }

    const merged = sortExpenses(Array.from(mergeMap.values()));
    setExpenses(merged);
    writeCache(merged, { lastUpdatedAtMs: newestUpdatedAtMs });
    return merged;
  }, [affectsCash, fetchFull, range?.startYMD, range?.endYMD, writeCache]);

  const loadExpenses = useCallback(async ({ forceFull = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const cached = readCache();
      if (cached) {
        metaRef.current = {
          cacheTs: cached.ts,
          fullSyncAt: cached.fullSyncAt,
          lastUpdatedAtMs: cached.lastUpdatedAtMs,
        };
        setExpenses(sortExpenses(cached.data));
      }

      const dirty = getExpensesDirtyMarker();
      const dirtyAfterCache = !!dirty?.ts && (!cached?.ts || Number(dirty.ts) > Number(cached.ts));
      const dirtyReason = String(dirty?.reason || '').toLowerCase();
      const deletionNeedsReconcile = dirtyAfterCache && dirtyReason.includes('deleted');
      const fullExpired = !cached?.fullSyncAt || Date.now() - cached.fullSyncAt > CACHE_TTL_MS;

      if (forceFull || !cached || deletionNeedsReconcile || fullExpired) {
        return await fetchFull();
      }

      // Entrada normal al módulo (o alta/edición detectada): consulta solo documentos
      // con updatedAt posterior al último sync. Si no hubo cambios, no relee la colección.
      return await fetchDelta(cached.data, cached.lastUpdatedAtMs);
    } catch (err) {
      console.error('useExpensesData error', err);
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [fetchDelta, fetchFull, readCache]);

  const upsertExpense = useCallback((row) => {
    if (!row?.id) return;
    setExpenses((prev) => {
      const nextMap = new Map(prev.map((item) => [item.id, item]));
      const current = nextMap.get(row.id) || {};
      nextMap.set(row.id, { ...current, ...row });
      const next = sortExpenses(Array.from(nextMap.values()));
      writeCache(next);
      return next;
    });
  }, [writeCache]);

  const removeExpense = useCallback((id) => {
    if (!id) return;
    setExpenses((prev) => {
      const next = prev.filter((row) => row.id !== id);
      writeCache(next);
      return next;
    });
  }, [writeCache]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onStorage = (event) => {
      if (event.key !== EXPENSES_DIRTY_KEY) return;
      let reason = '';
      try { reason = String(JSON.parse(event.newValue || '{}')?.reason || '').toLowerCase(); } catch {}
      loadExpenses({ forceFull: reason.includes('deleted') });
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [loadExpenses]);

  return {
    expenses,
    loading,
    error,
    reload: () => loadExpenses({ forceFull: true }),
    upsertExpense,
    removeExpense,
  };
}
